import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { l } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { FlowCardClickAction } from 'apps/cms/pages/ContentManagementSystem/lib/types'
import { contentManagementActions, contentManagementSelectors } from 'apps/cms/store'
import { EasyBuilderAnalytics } from 'apps/easyBuilder/analytics/easyBuilderAnalytics'
import { useSidebarContext } from 'apps/easyBuilder/context/EasyBuilderSidebarContext'
import { warnAccessDenied, warnDisabled } from 'apps/templates/models/Template/helpers'
import FSPath from 'common/cms/models/FSPath'
import { PermissionTargets, usePermission } from 'common/userRoles'
import { FsFlow } from 'shared/api/requests/cms/schemas'
import { typedRouting } from 'utils/router'
import { getFlowUrlByPath } from 'utils/router/tools/cms'

import { useFlowCardValidation } from './useFlowCardValidation'

function getFlowUrl(flow: FsFlow) {
  const isEasyBuilderFlow = Boolean(flow.easy_builder)
  const isQuickAutomationFlow = Boolean(flow.is_quick_automation)

  if (isEasyBuilderFlow) {
    return typedRouting.linkBuilder.easyBuilder({ flow_id: flow.ns })
  }

  if (isQuickAutomationFlow) {
    // TODO[QUICK_AUTOMATION]: move to typedRouting
    return FSPath.getQuickAutomationPath(flow.path || flow.ns)
  }

  return getFlowUrlByPath(flow.path || flow.ns)
}

export const useFlowCard = ({ flow }: { flow: FsFlow }) => {
  const flowId = flow.ns
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { campaignType } = useSidebarContext()
  const userCanEditFlow = usePermission(PermissionTargets.FLOW_EDITING)

  const { flowCardClickAction } = useAppSelector(contentManagementSelectors.getUi)
  const { isCardDisabled, errorMessage } = useFlowCardValidation(flow)
  const isEasyBuilderFlow = Boolean(flow.easy_builder)
  const isQuickAutomationFlow = Boolean(flow.is_quick_automation)
  let cardActionAriaLabel = l.translate('Open {automationName}', { automationName: flow.name })
  if (flowCardClickAction === FlowCardClickAction.SAVE_FLOW_ID_TO_STATE) {
    cardActionAriaLabel = l.translate('Select {automationName}', { automationName: flow.name })
  }

  const accessData = isEasyBuilderFlow ? undefined : flow.template_installation_access_data

  const cardUrl = getFlowUrl(flow)

  const putFlowIdToState = useCallback(() => {
    dispatch(contentManagementActions.setLastSelectedFlowId(flowId))
  }, [dispatch, flowId])

  const openFlow = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>) => {
      if (accessData && accessData.is_protected && !accessData.is_owner) {
        if (accessData.is_disabled) {
          return warnDisabled()
        }

        if (accessData.is_locked) {
          return warnAccessDenied()
        }
      }

      if (isEasyBuilderFlow) {
        EasyBuilderAnalytics.EDIT_MODE_OPENED({ campaignType, flowId })
      }

      if (event.ctrlKey || event.metaKey) {
        window.open(cardUrl)
      } else {
        navigate(cardUrl)
      }
    },
    [accessData, isEasyBuilderFlow, campaignType, flowId, cardUrl, navigate],
  )

  const handleFlowCardClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>) => {
      event.preventDefault()

      switch (flowCardClickAction) {
        case FlowCardClickAction.SAVE_FLOW_ID_TO_STATE:
          putFlowIdToState()
          break
        case FlowCardClickAction.OPEN_FLOW:
          openFlow(event)
          break
      }
    },
    [flowCardClickAction, putFlowIdToState, openFlow],
  )

  return {
    userCanEditFlow,
    cardUrl,
    cardActionAriaLabel,
    handleFlowCardClick,
    accessData,
    isCardDisabled,
    isEasyBuilderFlow,
    isQuickAutomationFlow,
    errorMessage,
  }
}
